import { jwtDecode } from 'jwt-decode';
import React, { createContext, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import apiClient from 'api/apiClient';
import makeAuthenticatedRequest from '../api';

export const UserContext = createContext();

export const UserProvider = (props) => {
  const [user, setUser] = useState(null);
  const [userProjects, setUserProjects] = useState(null);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [tableConfigs, setTableConfigs] = useState({});
  const navigate = useNavigate();

  // Load user + projects + table configs on mount
  useEffect(() => {
    initializeUser();
  }, []);

  const publicRoutes = [
    '/auth/login',
    '/auth/register',
    '/auth/request-password-reset',
    '/auth/new-password',
    '/auth/password-updated',
    '/auth/mail-sent',
    '/auth/verify-email',
    '/quickdownload',
    '/quickdownload/view',
    '/auth/404',
    '/download/report/:caseid',
    '/download/billing/invoice/:id',
    '/download/billing/quote/:id',
    '/download/billing/contract/:id',
  ];

  // Kept for backward compat — apiClient interceptors handle refresh+retry
  // automatically, so this is only called if something bypasses apiClient.
  const refreshToken = useCallback(async () => {
    const currentRefreshToken = localStorage.getItem('refreshToken');
    if (!currentRefreshToken) {
      throw new Error('No refresh token available');
    }

    // Use raw import('axios') to avoid apiClient interceptor loop
    const axios = (await import('axios')).default;
    const response = await axios.post(
      `${apiClient.defaults.baseURL}/refresh`,
      {},
      { headers: { Authorization: `Bearer ${currentRefreshToken}` } },
    );

    if (response.data?.access_token) {
      localStorage.setItem('token', response.data.access_token);
      return true;
    }
    throw new Error('Invalid refresh response');
  }, []);

  const fetchUserProjects = useCallback(
    async (userData) => {
      setProjectsLoading(true);
      try {
        const res = await makeAuthenticatedRequest(
          `clients/${userData.client.id}/users/${userData.id}/projects`,
          refreshToken, // Pass the refreshToken function, not null
          { method: 'get' },
        );
        setUserProjects(res.data.projects || []);
      } catch (e) {
        console.error(e);
        setUserProjects([]); // Set empty array on error
      } finally {
        setProjectsLoading(false);
      }
    },
    [refreshToken],
  );

  const refreshProjects = useCallback(() => {
    if (user) fetchUserProjects(user);
  }, [user, fetchUserProjects]);

  const refreshUser = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const info = jwtDecode(token);
      if (info?.sub) {
        // Fetch fresh user data from backend
        const res = await makeAuthenticatedRequest(
          `users/${info.sub}`,
          refreshToken,
          { method: 'get' },
        );

        // Update user with fresh data including client info
        const userData = { ...info, ...res.data, id: info.sub };
        setUser(userData);
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  }, [refreshToken]);

  // NEW: call this after a successful login
  const login = (accessToken, refreshToken) => {
    localStorage.setItem('token', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('successStatus', 'true');

    const info = jwtDecode(accessToken);
    const userData = { ...info, id: info.sub };
    setUser(userData);
    fetchUserProjects(userData);
    loadTableConfigs(userData.id);
  };

  // NEW: call this when the user clicks "logout"
  const logout = () => {
    // Only clear auth-related items, preserve column configurations
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('successStatus');
    // Keep any items that start with 'columnConfig:' for column persistence

    setUser(null);
    setUserProjects(null);
    setProjectsLoading(false);
    setTableConfigs({});
    navigate('/auth/login');
  };

  const initializeUser = async () => {
    const token = localStorage.getItem('token');
    const success = localStorage.getItem('successStatus');

    if (success === 'true' && token) {
      const info = jwtDecode(token);
      if (info?.sub) {
        // Set user from JWT immediately for fast initial render
        const userData = { ...info, id: info.sub };
        setUser(userData);
        fetchUserProjects(userData);
        loadTableConfigs(userData.id);

        // Then fetch fresh data from API to pick up any status changes
        // (e.g. subscription_status updated after JWT was issued)
        const controller = new AbortController();
        try {
          const res = await makeAuthenticatedRequest(
            `users/${info.sub}`,
            refreshToken,
            { method: 'get', signal: controller.signal },
          );
          // Guard against stale update if user logged out while request was in flight
          if (!controller.signal.aborted) {
            setUser((prev) => (prev ? { ...prev, ...res.data, id: info.sub } : prev));
          }
        } catch (e) {
          if (e?.name !== 'AbortError') {
            // Non-critical — stale JWT data is still usable
            console.warn('Could not refresh user data on init:', e);
          }
        }
      }
    } else {
      const url = new URL(window.location.href);
      const pathname = url.pathname;
      const searchParams = url.searchParams;
      if (pathname === '/auth/new-password' && searchParams.has('token')) {
        return;
      }
      if (!publicRoutes.includes(pathname)) {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('successStatus');
        navigate('/auth/login');
      }
    }
  };

  // Persist and load table column configurations per user
  const loadTableConfigs = (userId) => {
    const raw = localStorage.getItem(`tableConfigs:${userId}`);
    if (raw) {
      try {
        setTableConfigs(JSON.parse(raw));
      } catch (_) {
        setTableConfigs({});
      }
    }
  };

  const saveTableConfig = (viewKey, columns) => {
    setTableConfigs((prev) => {
      const updated = { ...prev, [viewKey]: columns };
      if (user?.id) {
        localStorage.setItem(
          `tableConfigs:${user.id}`,
          JSON.stringify(updated),
        );
      }
      return updated;
    });
  };

  const getTableConfig = (viewKey, defaultColumns = []) => {
    return tableConfigs[viewKey] || defaultColumns;
  };

  const isImpersonating = Boolean(user?.original_user_id);

  const impersonate = async (email) => {
    // Backup current tokens before impersonation
    const currentToken = localStorage.getItem('token');
    const currentRefresh = localStorage.getItem('refreshToken');
    if (currentToken) {
      localStorage.setItem('_backup_token', currentToken);
    }
    if (currentRefresh) {
      localStorage.setItem('_backup_refreshToken', currentRefresh);
    }

    const res = await makeAuthenticatedRequest('impersonate', refreshToken, {
      method: 'post',
      data: { email },
    });
    const { access_token, refresh_token: newRefresh } = res.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('refreshToken', newRefresh);
    const info = jwtDecode(access_token);
    const userData = { ...info, id: info.sub };
    setUser(userData);
    fetchUserProjects(userData);
    loadTableConfigs(userData.id);
  };

  const stopImpersonating = async () => {
    try {
      const res = await makeAuthenticatedRequest('stop-impersonation', refreshToken, {
        method: 'post',
      });
      const { access_token, refresh_token: newRefresh } = res.data;
      localStorage.setItem('token', access_token);
      localStorage.setItem('refreshToken', newRefresh);
      const info = jwtDecode(access_token);
      const userData = { ...info, id: info.sub };
      setUser(userData);
      fetchUserProjects(userData);
      loadTableConfigs(userData.id);
    } catch {
      // Fallback: restore backed-up tokens if stop-impersonation fails
      const backupToken = localStorage.getItem('_backup_token');
      const backupRefresh = localStorage.getItem('_backup_refreshToken');
      if (backupToken && backupRefresh) {
        localStorage.setItem('token', backupToken);
        localStorage.setItem('refreshToken', backupRefresh);
        const info = jwtDecode(backupToken);
        const userData = { ...info, id: info.sub };
        setUser(userData);
        fetchUserProjects(userData);
        loadTableConfigs(userData.id);
      }
    } finally {
      localStorage.removeItem('_backup_token');
      localStorage.removeItem('_backup_refreshToken');
    }
  };

  const createProject = async (projectData) => {
    const res = await makeAuthenticatedRequest(
      `clients/${user.client.id}/projects`,
      refreshToken,
      { method: 'post', data: projectData },
    );
    // append the newly created project (which is already assigned to current_user)
    const newProject = res.data.project;
    setUserProjects((prev) => [...prev, newProject]);
    return newProject;
  };

  const updateProject = async (projectId, updatedData) => {
    try {
      const res = await makeAuthenticatedRequest(
        `clients/${user.client.id}/projects/${projectId}`,
        refreshToken,
        { method: 'put', data: updatedData },
      );
      setUserProjects((prev) =>
        prev.map((p) => (p.id === projectId ? res.data.project : p)),
      );
    } catch (e) {
      console.error('Error updating project', e);
    }
  };

  const deleteProject = async (projectId) => {
    try {
      await makeAuthenticatedRequest(
        `clients/${user.client.id}/projects/${projectId}`,
        refreshToken,
        { method: 'delete' },
      );
      setUserProjects((prev) => prev.filter((p) => p.id !== projectId));
    } catch (e) {
      console.error('Error deleting project', e);
    }
  };

  return (
    <UserContext.Provider
      value={{
        user,
        userProjects,
        projectsLoading,
        refreshProjects,
        refreshUser,
        setUser,
        refreshToken,
        getTableConfig,
        saveTableConfig,
        createProject,
        updateProject,
        deleteProject,
        login,
        logout,
        impersonate,
        stopImpersonating,
        isImpersonating,
      }}
    >
      {props.children}
    </UserContext.Provider>
  );
};
